import { useMemo, useEffect } from 'react';
import { Formik, useFormikContext } from 'formik';

import { useAlert } from 'hooks';
import { ButtonColors } from 'types';
import { Row, Col } from 'components/grid';
import { formatPrice } from 'utils/helpers';
import * as CommonStyles from 'components/styles';
import { Dialog, DialogType } from 'components/dialog';
import { Button, ButtonTypes } from 'components/button';
import { InputField, SelectField } from 'components/fields';

import { FormValues as CommonFormValues } from '../types';
import { calculateAvailableRefundAmounts } from '../helpers';

import * as Styles from './styles';
import { FormProps } from './types';
import {
  getInitialValues,
  getValidationSchema,
  getPaymentTypeOptions,
} from './config';

export const Form = ({
  data,
  isEdit,
  trigger,
  onSubmit,
  checkout,
  maxAllowedTipRefund,
  allowedPaymentMethodTypes,
  maxAllowedProcessingFeeRefund,
}: FormProps) => {
  const { showErrorAlert } = useAlert();
  const { values: commonValues } = useFormikContext<CommonFormValues>();

  const {
    remainingAmount,
    remainingTipAmount,
    selectedItemsAmount,
    remainingProcessingFeeAmount,
  } = calculateAvailableRefundAmounts({
    data: checkout,
    maxAllowedTipRefund,
    items: commonValues.items,
    maxAllowedProcessingFeeRefund,
    excludeId: isEdit ? data?.id : undefined,
    returnPayments: commonValues.returnPayments,
  });

  const paymentTypeOptions = useMemo(
    () => getPaymentTypeOptions(allowedPaymentMethodTypes),
    [allowedPaymentMethodTypes]
  );

  const isPaymentBalanceEmpty =
    remainingAmount <= 0 &&
    remainingTipAmount <= 0 &&
    remainingProcessingFeeAmount <= 0;

  return (
    <Dialog trigger={trigger} title={`${isEdit ? 'Edit' : 'Add'} Payment`}>
      {(close, _, ButtonWrap) => (
        <Formik
          onSubmit={async (values) => {
            await onSubmit(values);
            close();
          }}
          validationSchema={getValidationSchema({
            maxAmount: remainingAmount,
            maxTipAmount: remainingTipAmount,
            maxProcessingFeeAmount: remainingProcessingFeeAmount,
          })}
          initialValues={getInitialValues({
            data,
            amount: remainingAmount,
            tipAmount: remainingTipAmount,
            processingFeeAmount: remainingProcessingFeeAmount,
          })}
        >
          {({ dirty, isSubmitting }) => {
            useEffect(() => {
              if (isPaymentBalanceEmpty) {
                showErrorAlert(
                  'The balance of the final refund and tip is empty, you cannot add another payment method'
                );
                close();
              }
            }, []);

            return (
              <CommonStyles.Form>
                <Styles.AvailableAmountContainer>
                  <Styles.AvailableAmount>
                    <span>Available Amount:</span>{' '}
                    {formatPrice(remainingAmount)}
                  </Styles.AvailableAmount>

                  {!!maxAllowedTipRefund && (
                    <Styles.AvailableAmount>
                      <span>Available Tip:</span>{' '}
                      {formatPrice(remainingTipAmount)}
                    </Styles.AvailableAmount>
                  )}

                  {!!maxAllowedProcessingFeeRefund && (
                    <Styles.AvailableAmount>
                      <span>Available Processing Fee:</span>{' '}
                      {formatPrice(maxAllowedProcessingFeeRefund)}
                    </Styles.AvailableAmount>
                  )}

                  <Styles.AvailableAmount>
                    <span>Selected Products Amount:</span>{' '}
                    {formatPrice(selectedItemsAmount)}
                  </Styles.AvailableAmount>
                </Styles.AvailableAmountContainer>

                <Row>
                  <Col>
                    <InputField
                      number
                      decimal
                      name="amount"
                      label="Amount"
                      placeholder="Amount"
                    />
                  </Col>
                </Row>

                {!!maxAllowedTipRefund && (
                  <Row>
                    <Col>
                      <InputField
                        number
                        decimal
                        name="tip"
                        label="Tip Amount"
                        placeholder="Tip Amount"
                      />
                    </Col>
                  </Row>
                )}

                {!!maxAllowedProcessingFeeRefund && (
                  <Row>
                    <Col>
                      <InputField
                        number
                        decimal
                        name="processingFee"
                        label="Processing Fee Amount"
                        placeholder="Processing Fee Amount"
                      />
                    </Col>
                  </Row>
                )}

                <Row>
                  <Col>
                    <SelectField
                      onlyValue
                      name="type"
                      label="Type"
                      placeholder="Select Type"
                      options={paymentTypeOptions}
                    />
                  </Col>
                </Row>

                <ButtonWrap type={DialogType.Plain}>
                  <Button
                    text="Cancel"
                    onClick={close}
                    colorType={ButtonColors.LightBlue}
                  />
                  <Button
                    text="Add"
                    type={ButtonTypes.Submit}
                    disabled={!dirty || isSubmitting}
                  />
                </ButtonWrap>
              </CommonStyles.Form>
            );
          }}
        </Formik>
      )}
    </Dialog>
  );
};
